// SPECS: none — extracted as-is from app/(dashboard)/billing/invoices/page.tsx (P0-24); dialog behavior not yet pinned by tests
'use client';

import { useState, useCallback, useRef } from 'react';
import { formatCents } from '@/lib/format';
import { Trash2, PlusCircle } from 'lucide-react';
import { Input, Select , Button } from '@/components/ui';

interface LineItemDraft {
  description: string;
  quantity: number;
  unit_price_cents: number;
  /**
   * service_id + service_date let the backend auto-link this line item to
   * the pending billing_event it represents (POST /api/field/invoices),
   * exactly like automated invoice generation does — see
   * lib/billing/event-resolver.ts. Both optional: free-typed lines (taxes,
   * one-off adjustments) legitimately have neither.
   */
  service_id: string | null;
  service_date: string;
}

interface CustomerOption {
  id: string;
  name: string;
}

interface PropertyOption {
  id: string;
  address_street: string;
  customer_id: string | null;
}

interface BillingEvent {
  id: string;
  description: string;
  service_id: string | null;
  service_name: string | null;
  service_date: string | null;
  total_cents: number;
  created_at: string;
}

export function CreateInvoiceDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([
    { description: '', quantity: 1, unit_price_cents: 0, service_id: null, service_date: '' },
  ]);
  const [unbilledEvents, setUnbilledEvents] = useState<BillingEvent[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Search customers with debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCustomerSearch = useCallback((value: string) => {
    setCustomerSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length < 2) { setCustomers([]); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const res = await fetch(`/api/field/customers?search=${encodeURIComponent(value)}&limit=10`);
        if (res.ok) {
          const data = await res.json();
          setCustomers((data.customers || []).map((c: { id: string; name: string }) => ({ id: c.id, name: c.name })));
        }
      } catch { /* ignore */ }
      setSearchingCustomers(false);
    }, 300);
  }, []);

  // Fetch unbilled events when property changes
  const fetchUnbilledEvents = useCallback(async (propertyId: string) => {
    setLoadingEvents(true);
    setUnbilledEvents([]);
    setSelectedEventIds(new Set());
    try {
      const res = await fetch(`/api/field/billing-events?property_id=${propertyId}&status=pending`);
      if (res.ok) {
        const data = await res.json();
        setUnbilledEvents(data.events || []);
      }
    } catch { /* ignore */ }
    setLoadingEvents(false);
  }, []);

  // Load properties when customer is selected
  const handleSelectCustomer = useCallback(async (customer: CustomerOption) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setCustomers([]);
    try {
      const res = await fetch(`/api/field/properties?customer_id=${customer.id}`);
      if (res.ok) {
        const data = await res.json();
        const props = (data.properties || data || []).map((p: { id: string; address_street: string; customer_id: string | null }) => ({
          id: p.id, address_street: p.address_street, customer_id: p.customer_id,
        }));
        setProperties(props);
        if (props.length === 1) {
          setSelectedPropertyId(props[0].id);
          fetchUnbilledEvents(props[0].id);
        }
      }
    } catch { /* ignore */ }
  }, [fetchUnbilledEvents]);

  const handlePropertyChange = useCallback((propertyId: string) => {
    setSelectedPropertyId(propertyId);
    if (propertyId) fetchUnbilledEvents(propertyId);
    else { setUnbilledEvents([]); setSelectedEventIds(new Set()); }
  }, [fetchUnbilledEvents]);

  const toggleEvent = (eventId: string) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const importSelectedEvents = () => {
    const selected = unbilledEvents.filter(e => selectedEventIds.has(e.id));
    if (selected.length === 0) return;
    const newItems: LineItemDraft[] = selected.map(e => ({
      description: e.service_name || e.description,
      quantity: 1,
      unit_price_cents: e.total_cents,
      service_id: e.service_id,
      service_date: e.service_date || '',
    }));
    setLineItems(prev => {
      // Replace empty first item if it exists
      if (prev.length === 1 && !prev[0].description && prev[0].unit_price_cents === 0) {
        return newItems;
      }
      return [...prev, ...newItems];
    });
    setSelectedEventIds(new Set());
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { description: '', quantity: 1, unit_price_cents: 0, service_id: null, service_date: '' }]);
  };

  const removeLineItem = (idx: number) => {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLineItem = (idx: number, field: keyof LineItemDraft, value: string | number | null) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const subtotalCents = lineItems.reduce((sum, li) => sum + (li.quantity * li.unit_price_cents), 0);

  const handleSubmit = async () => {
    if (!selectedPropertyId) { setError('Select a property'); return; }
    if (lineItems.length === 0 || lineItems.every(li => !li.description)) { setError('Add at least one line item'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/field/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: selectedPropertyId,
          customer_id: selectedCustomer?.id || undefined,
          billing_type: 'per_stop',
          due_date: dueDate,
          subtotal_cents: subtotalCents,
          tax_cents: 0,
          total_cents: subtotalCents,
          balance_due_cents: subtotalCents,
          notes: notes || undefined,
          line_items: lineItems.filter(li => li.description).map((li, idx) => ({
            description: li.description,
            quantity: li.quantity,
            unit_price_cents: li.unit_price_cents,
            total_cents: li.quantity * li.unit_price_cents,
            sort_order: idx,
            service_id: li.service_id || undefined,
            service_date: li.service_date || undefined,
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create invoice');
      }
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="create-invoice-dialog-title" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 id="create-invoice-dialog-title" className="text-sm font-bold text-white">Create Invoice</h2>
          <Button onClick={onClose} className="text-zinc-500 hover:text-white">Close</Button>
        </div>

        {/* Customer search */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-400">Customer</label>
          <div className="relative">
            <Input
              value={customerSearch}
              onChange={(e) => handleCustomerSearch(e.target.value)}
              placeholder="Search by name..."
            />
            {searchingCustomers && <div className="absolute right-2 top-2 text-xs text-zinc-500">Searching...</div>}
            {customers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {customers.map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleSelectCustomer(c)}
                    className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 transition-colors"
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCustomer && (
            <div className="text-xs text-emerald-400">Selected: {selectedCustomer.name}</div>
          )}
        </div>

        {/* Property select */}
        {properties.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs text-zinc-400">Property</label>
            <Select value={selectedPropertyId} onChange={(e) => handlePropertyChange(e.target.value)}>
              <option value="">Select property...</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.address_street}</option>
              ))}
            </Select>
          </div>
        )}

        {/* Unbilled events */}
        {selectedPropertyId && (
          <div className="space-y-2">
            <label className="text-xs text-zinc-400">Unbilled Work</label>
            {loadingEvents ? (
              <div className="text-xs text-zinc-500 py-2">Loading...</div>
            ) : unbilledEvents.length === 0 ? (
              <div className="text-xs text-zinc-500 py-2 bg-zinc-800/50 rounded-lg px-3">No unbilled stops for this property</div>
            ) : (
              <>
                <div className="bg-zinc-800/50 rounded-lg divide-y divide-white/5 max-h-40 overflow-y-auto">
                  {unbilledEvents.map(evt => (
                    <label key={evt.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-700/50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedEventIds.has(evt.id)}
                        onChange={() => toggleEvent(evt.id)}
                        className="rounded border-zinc-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-zinc-200 truncate">{evt.service_name || evt.description}</div>
                        <div className="text-[10px] text-zinc-500">{evt.service_date || evt.created_at?.slice(0, 10)}</div>
                      </div>
                      <span className="text-xs text-zinc-300">{formatCents(evt.total_cents)}</span>
                    </label>
                  ))}
                </div>
                {selectedEventIds.size > 0 && (
                  <button onClick={importSelectedEvents} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                    <PlusCircle className="w-3 h-3" /> Import {selectedEventIds.size} item{selectedEventIds.size > 1 ? 's' : ''} as line items
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Due date */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-400">Due Date</label>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs text-zinc-400">Line Items</label>
            <button onClick={addLineItem} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
              <PlusCircle className="w-3 h-3" /> Add item
            </button>
          </div>
          {lineItems.map((li, idx) => (
            <div key={idx} className="space-y-1 pb-1 border-b border-white/5 last:border-b-0">
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    value={li.description}
                    onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description"
                    className="text-xs"
                  />
                </div>
                <div className="w-16">
                  <Input
                    type="number"
                    min={1}
                    value={li.quantity}
                    onChange={(e) => updateLineItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                    placeholder="Qty"
                    className="text-xs"
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={li.unit_price_cents === 0 ? '' : li.unit_price_cents / 100}
                    onChange={(e) => updateLineItem(idx, 'unit_price_cents', e.target.value === '' ? 0 : Math.round(parseFloat(e.target.value) * 100))}
                    placeholder="Price"
                    className="text-xs"
                  />
                </div>
                <div className="w-20 text-right text-xs text-zinc-300 pt-2">
                  {formatCents(li.quantity * li.unit_price_cents)}
                </div>
                {lineItems.length > 1 && (
                  <button onClick={() => removeLineItem(idx)} className="p-1 text-zinc-500 hover:text-rose-400 transition-colors mt-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 pl-0.5">
                <label className="text-[10px] text-zinc-500 whitespace-nowrap">Service date</label>
                <Input
                  type="date"
                  value={li.service_date}
                  disabled={!!li.service_id}
                  onChange={(e) => updateLineItem(idx, 'service_date', e.target.value)}
                  className="text-xs w-36 disabled:opacity-60"
                />
                {li.service_id && (
                  <>
                    <span className="text-[10px] text-emerald-400">linked to unbilled work</span>
                    <button
                      type="button"
                      onClick={() => updateLineItem(idx, 'service_id', null)}
                      className="text-[10px] text-zinc-500 hover:text-rose-400 underline"
                    >
                      unlink
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-1 border-t border-white/5">
            <span className="text-sm font-medium text-zinc-200">Total: {formatCents(subtotalCents)}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-400">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes..." />
        </div>

        {error && <div className="text-xs text-rose-400">{error}</div>}
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium py-2 rounded-lg transition-colors disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create Invoice'}
          </Button>
          <Button onClick={onClose} className="px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium py-2 rounded-lg transition-colors">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
